import { useAuthRegister } from '@/api'
import {
  KBButton,
  KBDivider,
  KBFlex,
  KBForm,
  KBInput,
  KBText,
  KBTypography,
} from '@/components/atoms'
import { KBAlert } from '@/components/atoms/KBAlert'
import { CaptchaWidgetWrapper } from '@/components/molecules/CaptchaWidget'
import { KBIntlRenderReactDom } from '@/components/organisms/KBIntlRenderReactDom'
import { useCaptcha } from '@/hooks/useCaptcha'
import { BACKEND_URL } from '@/utils/constants'
import { AxiosError } from 'axios'
import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import WoxIconGoogle from '~icons/wox-icons/google'
import WoxIconMicrosoft from '~icons/wox-icons/microsoft'

const { Text } = KBTypography

const ERROR_CODE_EMAIL_EXISTS = 422059
const ERROR_CODE_DOMAIN_EXISTS = 422479

type RegisterErrorType = 'emailExists' | 'domainExists' | null

const RegisterForm: React.FC = () => {
  const { t } = useTranslation()
  const [form] = KBForm.useForm()
  const navigate = useNavigate()
  const [registerError, setRegisterError] = useState<RegisterErrorType>(null)
  const TERMS_AND_PRIVACY = {
    terms: `${import.meta.env.VITE_TERMS_URL}`,
    privacy: `${import.meta.env.VITE_PRIVACY_URL}`,
  }
  const { mutate: register, isPending } = useAuthRegister()

  // CAPTCHA state
  const {
    config: captchaConfig,
    isLoading: captchaLoading,
    token: captchaToken,
    onVerify: onCaptchaVerify,
    onExpire: onCaptchaExpire,
    onError: onCaptchaError,
    reset: resetCaptcha,
    isEnabled: captchaEnabled,
    isReady: captchaReady,
  } = useCaptcha()

  const onSubmit = () => {
    setRegisterError(null)
    form.validateFields().then((values) => {
      register(
        {
          data: {
            ...values,
            // Include CAPTCHA token if enabled
            captchaToken: captchaEnabled ? captchaToken : undefined,
          },
        },
        {
          onSuccess: () => {
            navigate('/register/verification', {
              state: {
                email: values.email,
              },
            })
          },
          onError: (error: AxiosError<{ errorCode?: number }>) => {
            // Reset CAPTCHA on error so user can retry
            resetCaptcha()
            const errorCode = error.response?.data?.errorCode
            if (errorCode === ERROR_CODE_EMAIL_EXISTS) {
              setRegisterError('emailExists')
            } else if (errorCode === ERROR_CODE_DOMAIN_EXISTS) {
              setRegisterError('domainExists')
            }
          },
        }
      )
    })
  }

  const googleSignup = () => {
    window.location.href = `${BACKEND_URL}/api/v5/auth/google?mode=signup&redirect_uri=${window.location.origin}`
  }

  const microsoftSignup = () => {
    window.location.href = `${BACKEND_URL}/api/v5/auth/azure?mode=signup&redirect_uri=${window.location.origin}`
  }

  return (
    <KBFlex gap={0} wrap={false} className="tw-h-full tw-w-full">
      <KBFlex
        align="center"
        justify="center"
        className="tw-w-[512px] tw-flex-shrink-0 tw-px-12"
      >
        <img
          src="/registerBg.png"
          width={416}
          className="tw-mx-0 tw-my-auto"
          title=""
        />
      </KBFlex>
      <div className="tw-box-border tw-h-full tw-flex-1 tw-border tw-border-y-0 tw-border-r-0 tw-border-solid tw-border-[var(--wox-color-border-secondary)] tw-px-12 tw-py-10">
        <Text className="tw-mb-2 tw-text-2xl tw-font-bold tw-text-gray-800">
          {t('register.startUsingWOX')}
        </Text>
        <Text className="tw-mb-8 tw-block tw-text-sm tw-text-gray-500">
          {t('register.description')}
        </Text>
        <KBForm layout="vertical" form={form}>
          {registerError && (
            <KBAlert
              type="warning"
              showIcon
              className="tw-mb-4"
              message={
                registerError === 'emailExists'
                  ? t('register.emailAlreadyRegistered')
                  : t('register.companyAlreadyRegistered')
              }
              description={
                <Trans
                  i18nKey="register.pleaseLogin"
                  components={{
                    a: (
                      <a
                        href="/login"
                        className="tw-font-medium tw-text-[var(--wox-color-primary)]"
                      />
                    ),
                  }}
                />
              }
            />
          )}
          <KBForm.Item
            label={t('register.companyEmail')}
            name="email"
            rules={[
              {
                required: true,
                type: 'email',
              },
            ]}
          >
            <KBInput />
          </KBForm.Item>
          {/* CAPTCHA Widget */}
          {captchaEnabled && (
            <div className="tw-mb-4">
              <CaptchaWidgetWrapper
                enabled={captchaEnabled}
                loading={captchaLoading}
                siteKey={captchaConfig?.siteKey}
                provider={captchaConfig?.provider}
                onVerify={onCaptchaVerify}
                onExpire={onCaptchaExpire}
                onError={onCaptchaError}
              />
            </div>
          )}

          <KBForm.Item>
            <KBButton
              size="large"
              type="primary"
              className="tw-w-full"
              onClick={onSubmit}
              loading={isPending}
              disabled={!captchaReady}
            >
              {t('register.gotoStart')}
            </KBButton>
          </KBForm.Item>

          <KBDivider className="tw-my-4">{t('common.or')}</KBDivider>

          <KBFlex vertical gap={12}>
            <KBButton
              className="tw-w-full"
              onClick={googleSignup}
              size="large"
              icon={<WoxIconGoogle />}
            >
              {t('register.signupWithGoogle')}
            </KBButton>
            <KBButton
              className="tw-w-full"
              onClick={microsoftSignup}
              size="large"
              icon={<WoxIconMicrosoft />}
            >
              {t('register.signupWithMicrosoft')}
            </KBButton>
          </KBFlex>

          <div className="tw-mt-6">
            <Trans
              className="tw-mb-[9px] tw-flex-shrink-0 tw-text-[sm]"
              i18nKey="register.loginText"
              components={{
                a: (
                  <a
                    target="_blank"
                    href="/login"
                    rel="noopener noreferrer"
                    className="tw-text-[var(--wox-color-primary)]"
                  />
                ),
              }}
            />

            <KBText type="secondary" className="tw-mt-2 tw-block">
              <KBIntlRenderReactDom
                intlKey={'register.form.desc'}
                placeholders={{
                  terms: (
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={TERMS_AND_PRIVACY['terms']}
                      className="tw-text-[var(--wox-color-text-description)] tw-underline tw-underline-offset-1"
                    >
                      {t('login.authorizationStatement')}
                    </a>
                  ),
                  privacy: (
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={TERMS_AND_PRIVACY['privacy']}
                      className="tw-text-[var(--wox-color-text-description)] tw-underline tw-underline-offset-1"
                    >
                      {t('login.privacyProtection')}
                    </a>
                  ),
                }}
              />
            </KBText>
          </div>
        </KBForm>
      </div>
    </KBFlex>
  )
}

export default RegisterForm
